import { useEffect, useState } from 'react';
import { Loading } from '@bumble/app/components/Loading';
import { useApi } from '@bumble/app/hooks/use-api';
import {
  ConsoleHistoryConnection,
  ConsoleHistoryCpe,
  ConsoleHistoryResponse,
  historyCsv,
  historyDuration,
  historyType,
  historyWhen,
} from '@bumble/app/utils/console-access-history';
import CloseIcon from '@mui/icons-material/Close';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

type ConsoleAccessHistoryProps = {
  label: string;
  onClose: () => void;
} & (
  | { connection: ConsoleHistoryConnection; allHistory?: false }
  | { connection: ConsoleHistoryCpe; allHistory: true }
);

const ACTIVE_SESSION_REFRESH_MS = 30_000;
const DURATION_TICK_MS = 1000;

function historyError(error: unknown): string {
  const detail = (error as { error?: { detail?: unknown } })?.error?.detail;
  return typeof detail === 'string' ? detail : 'Unable to load access history. Please try again.';
}

export default function ConsoleAccessHistory({
  connection,
  allHistory = false,
  label,
  onClose,
}: ConsoleAccessHistoryProps) {
  const api = useApi();
  const [now, setNow] = useState(Date.now);
  const { data, dataUpdatedAt, isLoading, isFetching, isError, error, refetch } = useQuery(
    [allHistory ? 'console-access-history-all' : 'console-access-history', connection],
    async ({ signal }) => {
      const response = await api.request<ConsoleHistoryResponse>({
        path: allHistory ? '/view-console-access-history-all' : '/view-console-access-history',
        method: 'GET',
        query: connection,
        secure: true,
        format: 'json',
        signal,
      });
      return response.data;
    },
    {
      keepPreviousData: false,
      meta: { handleErrorsLocally: true },
      refetchOnMount: 'always',
      retry: false,
      refetchInterval: (history) =>
        history?.items.some((item) => item.status === 'active') ? ACTIVE_SESSION_REFRESH_MS : false,
    },
  );
  const items = isError ? [] : data?.items ?? [];
  const hasActiveSession = items.some((item) => item.status === 'active');

  useEffect(() => {
    if (!hasActiveSession) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), DURATION_TICK_MS);
    return () => window.clearInterval(timer);
  }, [hasActiveSession]);

  const downloadReport = () => {
    const blob = new Blob([historyCsv(items, dataUpdatedAt, Date.now(), allHistory)], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    let reportName = connection.cpe_id;
    if (allHistory) {
      reportName = `${connection.cpe_id}-network`;
    } else if (connection.access_type === 'usb') {
      reportName = label;
    } else if ('target_ip' in connection) {
      reportName = `${connection.cpe_id}-${connection.target_ip}-${connection.target_port}-${connection.protocol}`;
    }
    link.href = url;
    link.download = `access-history-${reportName.replace(/[^a-zA-Z0-9._-]/g, '-')}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), DURATION_TICK_MS);
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      onClose={onClose}
      aria-labelledby="console-access-history-title"
    >
      <DialogTitle
        id="console-access-history-title"
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          Access History
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
            {label}
          </Typography>
        </Box>
        <IconButton aria-label="Close access history" onClick={onClose} sx={{ flexShrink: 0 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadOutlinedIcon />}
            onClick={downloadReport}
            disabled={isFetching || isError || items.length === 0}
          >
            Download Report
          </Button>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Refresh history">
            <span>
              <IconButton
                aria-label="Refresh history"
                onClick={() => refetch()}
                disabled={isFetching}
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {isError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={<Button onClick={() => refetch()}>Retry</Button>}
          >
            {historyError(error)}
          </Alert>
        )}
        <TableContainer sx={{ minHeight: 250, maxHeight: 400 }}>
          <Table
            stickyHeader
            aria-label={allHistory ? 'CPE network access history' : 'Connection access history'}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: allHistory ? '22%' : '34%' }}>When</TableCell>
                <TableCell sx={{ width: allHistory ? '20%' : '33%' }}>IAM User</TableCell>
                {allHistory && (
                  <>
                    <TableCell sx={{ width: '10%' }}>Type</TableCell>
                    <TableCell sx={{ width: '18%' }}>Target IP</TableCell>
                    <TableCell sx={{ width: '8%' }}>Port</TableCell>
                  </>
                )}
                <TableCell sx={{ width: allHistory ? '22%' : '33%' }}>Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={allHistory ? 6 : 3}>
                    <Loading content="Loading access history…" height={80} />
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.session_id}>
                    <TableCell>{historyWhen(item.when)}</TableCell>
                    <TableCell sx={{ overflowWrap: 'anywhere' }}>{item.user}</TableCell>
                    {allHistory && (
                      <>
                        <TableCell>{historyType(item)}</TableCell>
                        <TableCell>{item.target?.ip || 'N/A'}</TableCell>
                        <TableCell>{item.target?.port ?? 'N/A'}</TableCell>
                      </>
                    )}
                    <TableCell>{historyDuration(item, dataUpdatedAt, now)}</TableCell>
                  </TableRow>
                ))
              )}
              {!isLoading && !isError && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={allHistory ? 6 : 3} align="center" sx={{ py: 6 }}>
                    {allHistory
                      ? 'No network access history for this CPE.'
                      : 'No access history for this connection.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
}
